import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Artikel, CreateVertragsartikelRequest } from '../types';
import artikelService from '../services/artikelService';
import { Icon, Input, Select, inputClass } from './ui';
import { formatPreis, formatMwst, zahlZuText, textZuZahl } from '../utils/preis';

/**
 * Eine beauftragte Leistung, solange der Vertrag noch nicht gespeichert ist. Die Eingaben bleiben
 * Text (deutsche Schreibweise), geprüft wird erst beim Absenden in `positionenZuRequests`.
 */
export interface PositionEntwurf {
  key: number;
  artikel: Artikel;
  mengeText: string;
  preisText: string;
  art: 'EINMALIG' | 'JAEHRLICH';
}

let naechsterKey = 1;

const neuePosition = (artikel: Artikel, teil: Partial<PositionEntwurf> = {}): PositionEntwurf => ({
  key: naechsterKey++,
  artikel,
  mengeText: '1',
  preisText: zahlZuText(artikel.preis ?? 0),
  art: 'EINMALIG',
  ...teil,
});

/** Eingaben prüfen und in Requests übersetzen; bei einem Fehler kommt die Meldung als String. */
export const positionenZuRequests = (positionen: PositionEntwurf[]): CreateVertragsartikelRequest[] | string => {
  const requests: CreateVertragsartikelRequest[] = [];
  for (const p of positionen) {
    const menge = textZuZahl(p.mengeText);
    if (menge == null || menge <= 0) return `„${p.artikel.bezeichnung}“: Menge muss größer als 0 sein`;
    const einzelpreis = textZuZahl(p.preisText);
    if (einzelpreis == null) return `„${p.artikel.bezeichnung}“: Preis ist keine gültige Zahl`;
    requests.push({
      artikelId: p.artikel.id,
      abrechnungsart: p.art,
      menge,
      einzelpreis,
      mwstSatz: p.artikel.mwstSatz,
    });
  }
  return requests;
};

/**
 * Die beauftragten Leistungen eines neuen Vertrags: Artikel aus der Preisliste, Menge, Preis und
 * ob einmalig oder jährlich berechnet wird. Alles beginnt mit dem ersten Vertragsjahr und steht
 * damit auf der Erstrechnung; die jährlichen Leistungen kommen danach jedes Jahr zum Jahrestag der
 * Unterschrift, im Voraus für das kommende Vertragsjahr.
 *
 * Beträge werden hier bewusst nicht gerechnet – die Summen liefert der Rechnungsentwurf aus dem
 * Backend.
 */
const PositionenErfassung: React.FC<{
  positionen: PositionEntwurf[];
  onChange: (positionen: PositionEntwurf[]) => void;
  /** Unterschriftsdatum (ISO), für die Klartext-Erklärung je Zeile. */
  unterschriftsdatum?: string;
  /** Fußzeile des Blocks, z. B. die automatische Verlängerung. */
  children?: React.ReactNode;
}> = ({ positionen, onChange, unterschriftsdatum, children }) => {
  const [artikel, setArtikel] = useState<Artikel[]>([]);
  const [suche, setSuche] = useState('');
  const [markiert, setMarkiert] = useState(0);
  const sucheRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    artikelService.getAll().then(setArtikel).catch(() => setArtikel([]));
  }, []);

  // Trefferliste erst ab einem Suchbegriff – die Preisliste hat mehrere hundert Artikel.
  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return [];
    return artikel
      .filter(a => a.active && `${a.artikelnummer} ${a.bezeichnung}`.toLowerCase().includes(q))
      .slice(0, 25);
  }, [artikel, suche]);

  useEffect(() => setMarkiert(0), [suche]);

  const aendere = (key: number, teil: Partial<PositionEntwurf>) =>
    onChange(positionen.map(p => (p.key === key ? { ...p, ...teil } : p)));

  const hinzufuegen = (a: Artikel) => {
    onChange([...positionen, neuePosition(a)]);
    setSuche('');
    // Fokus bleibt im Suchfeld – die nächste Leistung kann gleich folgen.
    sucheRef.current?.focus();
  };

  // Enter übernimmt den markierten Treffer statt das Vertragsformular abzuschicken.
  const tastatur = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setMarkiert(i => Math.min(i + 1, treffer.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMarkiert(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (treffer[markiert]) hinzufuegen(treffer[markiert]); }
    else if (e.key === 'Escape' && suche) { e.preventDefault(); setSuche(''); }
  };

  // Jahrestag als "23.09." für die Erklärung der jährlichen Leistungen.
  const tagMonat = unterschriftsdatum
    ? `${unterschriftsdatum.slice(8, 10)}.${unterschriftsdatum.slice(5, 7)}.`
    : null;

  const erklaerung = (p: PositionEntwurf): string =>
    p.art === 'EINMALIG'
      ? 'Einmalig, mit der Erstrechnung.'
      : `Mit der Erstrechnung, danach jedes Jahr${tagMonat ? ` am ${tagMonat}` : ' am Jahrestag'} im Voraus.`;

  return (
    <section className="overflow-hidden rounded-xl border border-brand-200 bg-white shadow-sm">
      <header className="flex items-baseline justify-between gap-3 border-b border-brand-100 bg-brand-50/70 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-brand-900">Leistungen</h3>
          <p className="text-xs text-brand-800/80">Was der Kunde beauftragt – steht auf der ersten Rechnung.</p>
        </div>
        {positionen.length > 0 && (
          <span className="whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-xs font-medium tabular-nums text-brand-800 ring-1 ring-brand-200">
            {positionen.length} {positionen.length === 1 ? 'Position' : 'Positionen'}
          </span>
        )}
      </header>

      {positionen.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {positionen.map(p => (
            <li key={p.key} className="ui-aufklappen px-4 py-3">
              <div className="mb-2 flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{p.artikel.bezeichnung}</p>
                  <p className="text-xs text-gray-500">
                    {p.artikel.artikelnummer} · Listenpreis {formatPreis(p.artikel.preis)} netto · {formatMwst(p.artikel.mwstSatz)} MwSt
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onChange(positionen.filter(q => q.key !== p.key))}
                  title="Position entfernen"
                  aria-label="Position entfernen"
                  className="-m-2 rounded-lg p-3 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <Icon.Trash className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <label className="text-xs text-gray-500">Menge
                  <Input inputMode="decimal" value={p.mengeText} onChange={e => aendere(p.key, { mengeText: e.target.value })} />
                </label>
                <label className="text-xs text-gray-500">Einzelpreis netto
                  <Input inputMode="decimal" value={p.preisText} onChange={e => aendere(p.key, { preisText: e.target.value })} />
                </label>
                <label className="text-xs text-gray-500">Abrechnung
                  <Select value={p.art} onChange={e => aendere(p.key, { art: e.target.value as PositionEntwurf['art'] })}>
                    <option value="EINMALIG">Einmalig</option>
                    <option value="JAEHRLICH">Jährlich</option>
                  </Select>
                </label>
              </div>
              <p className="mt-1.5 text-xs text-gray-500">{erklaerung(p)}</p>
            </li>
          ))}
        </ul>
      )}

      {/* Das Suchfeld ist immer da – der Weg zur ersten Leistung darf nicht hinter einem Link stecken. */}
      <div className={`px-4 py-3 ${positionen.length > 0 ? 'border-t border-gray-100 bg-gray-50/60' : ''}`}>
        <label htmlFor="leistung-suche" className="mb-1 block text-xs font-medium text-gray-600">
          {positionen.length === 0 ? 'Erste Leistung aus der Preisliste wählen' : 'Weitere Leistung hinzufügen'}
        </label>
        <div className="relative">
          <Icon.Plus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-600" />
          <input
            id="leistung-suche"
            ref={sucheRef}
            placeholder="Artikelnummer oder Bezeichnung, z. B. „Grundpacht“"
            value={suche}
            onChange={e => setSuche(e.target.value)}
            onKeyDown={tastatur}
            autoComplete="off"
            role="combobox"
            aria-expanded={treffer.length > 0}
            aria-controls="leistung-treffer"
            className={`${inputClass} min-h-[44px] pl-9`}
          />
        </div>
        {treffer.length > 0 && (
          <ul id="leistung-treffer" role="listbox" className="mt-2 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white">
            {treffer.map((a, i) => (
              <li key={a.id} role="option" aria-selected={i === markiert}>
                <button
                  type="button"
                  onClick={() => hinzufuegen(a)}
                  onMouseEnter={() => setMarkiert(i)}
                  className={`flex min-h-[44px] w-full items-center gap-2 px-3 py-2.5 text-left active:bg-brand-100 ${i === markiert ? 'bg-brand-50' : ''}`}
                >
                  <span className="whitespace-nowrap text-xs tabular-nums text-gray-400">{a.artikelnummer}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{a.bezeichnung}</span>
                  <span className="whitespace-nowrap text-xs tabular-nums text-gray-500">{formatPreis(a.preis)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {suche.trim() !== '' && treffer.length === 0 && (
          <p className="mt-2 px-1 text-sm text-gray-500">Kein Artikel gefunden.</p>
        )}
      </div>

      {children && <div className="border-t border-gray-200 px-4 py-3">{children}</div>}
    </section>
  );
};

export default PositionenErfassung;
